import {Button, Dialog, FormField, Table, TableRow, type FormControl} from 'platform/components';
import {Box, Grid, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';

import {useWatch, type UseFormReturn} from 'react-hook-form';

import {ascend, findLast, sortWith} from 'ramda';

import type {
  CatalogueTreeResponseBody,
  ConfiguratorBundleResponseBody,
  ConfiguratorTrimResponseBody,
  PriceBasis,
  TrimFeatureInclusionResponseBody,
} from '@dms/api/new-vehicle';
import {
  getTrimOrderability,
  getTrimOrderabilityText,
  BasisPrice,
  SelectableCard,
  useNewVehicleCatalogueLabels,
} from '@dms/features/new-vehicle';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, useOnMount} from 'shared/web';

import {Cell} from '../../../NewVehicleCatalogue/utils/catalogueUi';
import type {ConfiguratorFormValues} from '../../NewVehicleConfigurator';
import {getSelectionContext} from '../../utils/getSelectionContext';
import {getTrimEngines} from '../../utils/getTrimEngines';
import {isStandardFeature} from '../../utils/isStandardFeature';
import {OPTIONAL_INCLUSION, STANDARD_INCLUSION} from '../../utils/newVehicleLabels';
import {SectionLabel, StepIntro} from '../StepIntro';

export type ModelYearTrimProps = {
  tree?: CatalogueTreeResponseBody;
  bundle?: ConfiguratorBundleResponseBody;
  form: UseFormReturn<ConfiguratorFormValues>;
  control: FormControl<ConfiguratorFormValues>;
  isPreview?: boolean;
  context?: {makeName: string; modelName: string; modelYearName: string};
  compareOpen: boolean;
  onCompareClose: () => void;
  canSelect: boolean;
  factoryFreezeReason?: string;
};

const t = (key: string, defaultValue: string) => i18n.t(key, {defaultValue});
const HIGHLIGHT_LIMIT = 3;

const getStandardFeatureNames = (
  bundle: ConfiguratorBundleResponseBody | undefined,
  trimId: string
) =>
  (bundle?.features ?? [])
    .filter((feature) => isStandardFeature(feature, trimId))
    .map(({name}) => name);

const getHighlights = (names: string[]) => {
  if (!names.length) {
    return t('entity.newVehicle.labels.baseSpecification', 'Base specification');
  }
  const more =
    names.length > HIGHLIGHT_LIMIT
      ? ` · +${names.length - HIGHLIGHT_LIMIT} ${t('general.labels.more', 'more')}`
      : '';
  return `${names.slice(0, HIGHLIGHT_LIMIT).join(' · ')}${more}`;
};

/** Cheapest first by the shown "from" price; the rest keeps the order deterministic. */
const sortTrimsByPrice = sortWith<ConfiguratorTrimResponseBody>([
  ascend(({fromPriceGross}) => Number(fromPriceGross)),
  ascend(({basePriceNet}) => Number(basePriceNet)),
  ascend(({name}) => name),
  ascend(({id}) => id),
]);

/** Standard items this trim adds on top of the given cheaper trim. */
const getUpgradesOver = (
  bundle: ConfiguratorBundleResponseBody | undefined,
  trim: ConfiguratorTrimResponseBody,
  previous: ConfiguratorTrimResponseBody | undefined
) => {
  if (!previous) {
    return undefined;
  }
  const previousNames = new Set(getStandardFeatureNames(bundle, previous.id));
  const added = getStandardFeatureNames(bundle, trim.id).filter((name) => !previousNames.has(name));
  return added.length ? {over: previous.name, added} : undefined;
};

function InclusionCell(props: {
  inclusion?: TrimFeatureInclusionResponseBody;
  basis: PriceBasis;
  currency: string;
}) {
  return match(props.inclusion)
    .with({inclusion: STANDARD_INCLUSION}, () => (
      <HStack spacing={1} align="center">
        <Icon value="navigation/check" size={4} color="text.success" />
        <Text size="small" color="success">
          {t('entity.newVehicle.enums.inclusion.STANDARD', 'Standard')}
        </Text>
      </HStack>
    ))
    .with(
      {inclusion: OPTIONAL_INCLUSION, priceNet: Pattern.string, priceGross: Pattern.string},
      ({priceNet, priceGross}) => (
        <BasisPrice
          net={priceNet}
          gross={priceGross}
          basis={props.basis}
          currency={props.currency}
          prefix="+"
          size="small"
        />
      )
    )
    .otherwise(() => (
      <Text size="small" color="tertiary">
        {EMPTY_PLACEHOLDER}
      </Text>
    ));
}

function ContextValue(props: {label: string; value: string}) {
  return (
    <VStack spacing={1}>
      <Text size="xSmall" color="secondary">
        {props.label}
      </Text>
      <Text alternative>{props.value}</Text>
    </VStack>
  );
}

export function ModelYearTrim(props: ModelYearTrimProps) {
  const formatDate = useDateTimeFormatter();
  const {getBodyStyle} = useNewVehicleCatalogueLabels();
  const today = new Date();
  const formatOrderabilityDate = (date: Date) => formatDate('dateMedium', date);
  const makeId = useWatch({control: props.form.control, name: 'makeId'});
  const modelId = useWatch({control: props.form.control, name: 'modelId'});
  const trimId = useWatch({control: props.form.control, name: 'trimId'});
  useOnMount(() => {
    const values = props.form.getValues();
    const context = getSelectionContext(props.tree, props.bundle, values);
    if (!context.make || !context.model) {
      return;
    }
    if (!values.makeId) {
      props.form.setValue('makeId', context.make.id);
    }
    if (!values.modelId && (!values.makeId || values.makeId === context.make.id)) {
      props.form.setValue('modelId', context.model.id);
    }
  });
  const selectedMake = props.tree?.makes.find((make) => make.id === makeId);
  const selectedModel = selectedMake?.models.find((model) => model.id === modelId);
  const trims = sortTrimsByPrice(props.bundle?.trims ?? []);
  const clearConfiguredSelection = () => {
    props.form.setValue('trimId', '');
    props.form.setValue('variantId', '');
    props.form.setValue('colourId', '');
    props.form.setValue('upholsteryId', '');
    props.form.setValue('featureIds', []);
    props.form.setValue('packageIds', []);
  };
  const selectTrim = (nextTrimId: string) => {
    const trim = trims.find(({id}) => id === nextTrimId);
    if (
      props.factoryFreezeReason ||
      !trim ||
      (!props.isPreview && !getTrimOrderability(trim, new Date()).orderable)
    ) {
      return;
    }
    if (nextTrimId !== trimId) {
      props.form.setValue('trimId', nextTrimId, {shouldDirty: true});
      props.form.setValue('variantId', '', {shouldDirty: true});
    }
    props.onCompareClose();
  };

  return (
    <VStack spacing={6}>
      <StepIntro
        title={t('entity.newVehicle.steps.modelTrimTitle', 'Step 1 — Model & trim level')}
        description={t(
          'entity.newVehicle.steps.modelTrimIntro',
          'Choose make → model → model year, then pick the trim level.'
        )}
      />
      {props.isPreview && props.context ? (
        <Box backgroundColor="palettes.neutral.10.100" borderRadius="medium" padding={4}>
          <Grid columns={3} spacing={4}>
            <ContextValue
              label={t('entity.vehicle.labels.make', 'Make')}
              value={props.context.makeName}
            />
            <ContextValue
              label={t('entity.vehicle.labels.model', 'Model')}
              value={props.context.modelName}
            />
            <ContextValue
              label={t('entity.newVehicle.labels.modelYear', 'Model year')}
              value={props.context.modelYearName}
            />
          </Grid>
        </Box>
      ) : (
        <Grid columns={3} spacing={4}>
          <FormField
            control={props.control}
            type="choice"
            name="makeId"
            label={t('entity.vehicle.labels.make', 'Make')}
            isRequired
            isDisabled={!props.canSelect || Boolean(props.factoryFreezeReason)}
            options={(props.tree?.makes ?? []).map(({id, name}) => ({value: id, label: name}))}
            onChange={() => {
              props.form.setValue('modelId', '');
              props.form.setValue('modelYearId', '');
              clearConfiguredSelection();
            }}
          />
          <FormField
            control={props.control}
            type="choice"
            name="modelId"
            label={t('entity.vehicle.labels.model', 'Model')}
            isRequired
            isDisabled={!props.canSelect || Boolean(props.factoryFreezeReason) || !selectedMake}
            options={(selectedMake?.models ?? []).map(({id, name}) => ({value: id, label: name}))}
            onChange={() => {
              props.form.setValue('modelYearId', '');
              clearConfiguredSelection();
            }}
          />
          <FormField
            control={props.control}
            type="choice"
            name="modelYearId"
            label={t('entity.newVehicle.labels.modelYear', 'Model year')}
            isRequired
            isDisabled={!props.canSelect || Boolean(props.factoryFreezeReason) || !selectedModel}
            options={(selectedModel?.modelYears ?? []).map(({id, name}) => ({
              value: id,
              label: name,
            }))}
            onChange={clearConfiguredSelection}
          />
        </Grid>
      )}
      <Show when={props.bundle}>
        {(bundle) => (
          <VStack spacing={3}>
            <SectionLabel
              label={t('entity.newVehicle.labels.trimLevel', 'Trim level')}
              count={trims.length}
            />
            <VStack spacing={2}>
              {trims.map((trim) => {
                const orderability = getTrimOrderability(trim, today);
                const isSelectable = props.isPreview || orderability.orderable;
                const reason =
                  !props.isPreview && !orderability.orderable
                    ? getTrimOrderabilityText(trim, today, formatOrderabilityDate)
                    : undefined;
                const upgrades = getUpgradesOver(
                  props.bundle,
                  trim,
                  // The chip is a price step, so an equal-priced trim is never the one it is "over".
                  // Author preview shows draft trims that are rarely orderable, so it ignores orderability.
                  findLast(
                    (cheaper) =>
                      Number(cheaper.fromPriceGross) < Number(trim.fromPriceGross) &&
                      (props.isPreview || getTrimOrderability(cheaper, today).orderable),
                    trims
                  )
                );
                return (
                  <SelectableCard
                    key={trim.id}
                    control="radio"
                    name="trim"
                    title={trim.name}
                    isSelected={trim.id === trimId}
                    isDisabled={
                      !props.canSelect || Boolean(props.factoryFreezeReason) || !isSelectable
                    }
                    reason={props.factoryFreezeReason ?? reason}
                    onSelect={() => selectTrim(trim.id)}
                    description={`${getBodyStyle(trim.bodyStyle)} · ${getHighlights(getStandardFeatureNames(props.bundle, trim.id))}`}
                    trailing={
                      <BasisPrice
                        prefix={t('entity.newVehicle.labels.from', 'from')}
                        net={trim.fromPriceNet}
                        gross={trim.fromPriceGross}
                        basis={bundle.priceBasis}
                        currency={bundle.currency}
                        alternative
                        color={isSelectable ? 'primary' : 'secondary'}
                        noWrap
                        align="right"
                      />
                    }
                    footer={
                      upgrades || reason ? (
                        <VStack spacing={1}>
                          <Show when={upgrades}>
                            <HStack spacing={1} align="center">
                              <Icon value="action/trending_up" size={3} color="text.success" />
                              <Text size="xSmall" color="success">
                                {`${t('entity.newVehicle.labels.over', 'over')} ${upgrades?.over}: ${upgrades?.added.map((name) => `+${name}`).join(', ')}`}
                              </Text>
                            </HStack>
                          </Show>
                          <Show when={reason}>
                            <HStack spacing={1} align="center">
                              <Icon value="content/block" size={3} color="text.danger" />
                              <Text size="xSmall" color="danger">
                                {reason}
                              </Text>
                            </HStack>
                          </Show>
                        </VStack>
                      ) : undefined
                    }
                  />
                );
              })}
            </VStack>
          </VStack>
        )}
      </Show>
      <Dialog
        isOpen={props.compareOpen}
        size="large"
        title={t('entity.newVehicle.actions.compareTrims', 'Compare trim levels')}
        onClose={props.onCompareClose}
      >
        <Show when={props.bundle}>
          {(bundle) => (
            <Table
              columns={[
                {element: t('entity.newVehicle.labels.equipment', 'Equipment')},
                ...trims.map((trim) => ({element: trim.name})),
              ]}
            >
              <TableRow>
                <Cell>{t('entity.newVehicle.labels.engines', 'Engines')}</Cell>
                {trims.map((trim) => (
                  <Cell key={trim.id}>
                    {getTrimEngines(props.bundle, trim.id)
                      .map(({engine}) => engine.name)
                      .join(', ') || EMPTY_PLACEHOLDER}
                  </Cell>
                ))}
              </TableRow>
              <TableRow>
                <Cell>{t('general.labels.basePrice', 'Base price')}</Cell>
                {trims.map((trim) => {
                  const orderability = getTrimOrderability(trim, today);
                  const disabledReason =
                    props.factoryFreezeReason ??
                    (!props.isPreview && !orderability.orderable
                      ? getTrimOrderabilityText(trim, today, formatOrderabilityDate)
                      : undefined);
                  return (
                    <Cell key={trim.id}>
                      <VStack spacing={2} align="flex-start">
                        <BasisPrice
                          net={trim.basePriceNet}
                          gross={trim.basePriceGross}
                          basis={bundle.priceBasis}
                          currency={bundle.currency}
                          size="small"
                          alternative
                        />
                        <Show when={disabledReason}>
                          <Text size="xSmall" color="danger">
                            {disabledReason}
                          </Text>
                        </Show>
                        <Button
                          type="button"
                          size="small"
                          variant={trim.id === trimId ? 'secondary' : 'primary'}
                          title={
                            trim.id === trimId
                              ? t('general.labels.selected', 'Selected')
                              : t('general.actions.select', 'Select')
                          }
                          aria-label={`${t('general.actions.select', 'Select')} ${trim.name}`}
                          isDisabled={!props.canSelect || Boolean(disabledReason)}
                          onClick={() => selectTrim(trim.id)}
                        />
                      </VStack>
                    </Cell>
                  );
                })}
              </TableRow>
              {props.bundle?.features
                .filter((feature) => feature.trimInclusions.length > 0)
                .map((feature) => (
                  <TableRow key={feature.id}>
                    <Cell>{feature.name}</Cell>
                    {trims.map((trim) => (
                      <Cell key={trim.id}>
                        <InclusionCell
                          inclusion={feature.trimInclusions.find(
                            (entry) => entry.trimId === trim.id
                          )}
                          basis={bundle.priceBasis}
                          currency={bundle.currency}
                        />
                      </Cell>
                    ))}
                  </TableRow>
                ))}
            </Table>
          )}
        </Show>
      </Dialog>
    </VStack>
  );
}
